import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {BRAND} from '../brand';
import {
  authApi,
  devApi,
  inquiriesApi,
  listingsApi,
  profileApi,
  savesApi,
  snapshotApi,
  type Snapshot,
} from '../lib/api';
import {todayISO} from '../lib/format';
import {usePersistentState} from '../lib/persist';
import {useToast} from '../ui/Toast';
import type {Inquiry, InquiryStatus, Listing, Profile, Role} from './types';

/* ===========================================================================
   One source of truth for every app.
   ---------------------------------------------------------------------------
   Screens never touch the server or auth directly — they read this and call
   these functions. Writes are OPTIMISTIC: the change lands in local state
   immediately, then goes to the API; if the server rejects it, the toast says
   so and the snapshot is re-read, so the screen can never quietly disagree
   with the database.
   =========================================================================== */

const MAX_COMPARE = 4;
const EMPTY_SNAPSHOT: Snapshot = {inquiries: [], listings: [], profiles: [], saved: []};

interface StoreValue extends Snapshot {
  /** false until the first snapshot has arrived. */
  ready: boolean;
  /** Listing ids picked for the compare app. */
  compare: string[];
  user: Profile | null;
  profile: Profile | null;
  canPost: boolean;

  signIn: (email: string, password: string) => Promise<string | null>;
  signUp: (input: {
    email: string;
    name: string;
    password: string;
    phone: string;
    role: Role;
  }) => Promise<string | null>;
  signOut: () => Promise<void>;
  updateProfile: (patch: Partial<Profile>) => Promise<void>;

  saveListing: (listing: Listing) => Promise<void>;
  removeListing: (id: string) => Promise<void>;
  countView: (id: string) => void;

  toggleSaved: (id: string) => void;
  toggleCompare: (id: string) => void;
  clearCompare: () => void;

  sendInquiry: (input: Omit<Inquiry, 'at' | 'id' | 'status'>) => Promise<void>;
  setInquiryStatus: (id: string, status: InquiryStatus) => Promise<void>;

  resetDemo: () => Promise<void>;
  profileOf: (id: string) => Profile | null;
}

const StoreContext = createContext<StoreValue | null>(null);

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error('useStore must be used inside <StoreProvider>');
  return value;
}

/** Read-only access for chrome that renders outside an app (dock badges). */
export function useStoreOptional(): StoreValue | null {
  return useContext(StoreContext);
}

function upsert<T extends {id: string}>(items: T[], next: T): T[] {
  const index = items.findIndex((item) => item.id === next.id);
  if (index < 0) return [next, ...items];
  const copy = items.slice();
  copy[index] = next;
  return copy;
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Có lỗi xảy ra';
}

let sequence = 0;

export function newId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${sequence.toString(36)}`;
}

export function StoreProvider({children}: {children: ReactNode}) {
  const {notify} = useToast();
  const [snapshot, setSnapshot] = useState<Snapshot>(EMPTY_SNAPSHOT);
  const [user, setUser] = useState<Profile | null>(null);
  const [ready, setReady] = useState(false);
  const [compare, setCompare] = usePersistentState<string[]>(`${BRAND.storageKey}:compare`, []);

  const userRef = useRef<Profile | null>(null);
  userRef.current = user;
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const viewedRef = useRef(new Set<string>());

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await snapshotApi.get());
    } catch (error) {
      notify(`Không tải được dữ liệu — ${message(error)}`, 'warn');
    } finally {
      setReady(true);
    }
  }, [notify]);

  useEffect(() => {
    let alive = true;
    void authApi
      .me()
      .then(({user: current}) => {
        if (alive) setUser(current);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) void refresh();
      });
    return () => {
      alive = false;
    };
  }, [refresh]);

  const mutate = useCallback(
    async (apply: (prev: Snapshot) => Snapshot, run: () => Promise<void>, failure: string) => {
      setSnapshot(apply);
      try {
        await run();
      } catch (error) {
        notify(`${failure} — ${message(error)}`, 'warn');
        await refresh();
      }
    },
    [notify, refresh],
  );

  /* ------------------------------------------------------------------ auth */

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const {user: next} = await authApi.signIn(email, password);
        setUser(next);
        await refresh();
        notify(`Xin chào ${next.name || 'bạn'}`);
        return null;
      } catch (error) {
        return message(error);
      }
    },
    [notify, refresh],
  );

  const signUp = useCallback<StoreValue['signUp']>(
    async ({email, name, password, phone, role}) => {
      try {
        const {user: next} = await authApi.signUp({email, name, password, phone, role});
        setUser(next);
        await refresh();
        notify('Tạo tài khoản thành công');
        return null;
      } catch (error) {
        return message(error);
      }
    },
    [notify, refresh],
  );

  const signOut = useCallback(async () => {
    await authApi.signOut();
    setUser(null);
    await refresh();
    notify('Đã đăng xuất', 'info');
  }, [notify, refresh]);

  const updateProfile = useCallback<StoreValue['updateProfile']>(
    async (patch) => {
      const current = userRef.current;
      if (!current) return;
      setUser({...current, ...patch});
      try {
        setUser(await profileApi.update(patch));
      } catch (error) {
        notify(`Không lưu được hồ sơ — ${message(error)}`, 'warn');
        setUser(current);
      }
    },
    [notify],
  );

  /* -------------------------------------------------------------- listings */

  const saveListing = useCallback<StoreValue['saveListing']>(
    async (listing) => {
      const next = {...listing, updatedAt: todayISO()};
      await mutate(
        (prev) => ({...prev, listings: upsert(prev.listings, next)}),
        async () => {
          const saved = await listingsApi.upsert(next);
          setSnapshot((prev) => ({...prev, listings: upsert(prev.listings, saved)}));
        },
        'Không lưu được tin đăng',
      );
    },
    [mutate],
  );

  const removeListing = useCallback<StoreValue['removeListing']>(
    async (id) => {
      await mutate(
        (prev) => ({
          ...prev,
          inquiries: prev.inquiries.filter((entry) => entry.listingId !== id),
          listings: prev.listings.filter((entry) => entry.id !== id),
          saved: prev.saved.filter((entry) => entry !== id),
        }),
        async () => {
          await listingsApi.remove(id);
        },
        'Không xoá được tin đăng',
      );
      setCompare((prev) => prev.filter((entry) => entry !== id));
    },
    [mutate, setCompare],
  );

  /** Counted once per listing per session — a re-render is not a visit. */
  const countView = useCallback<StoreValue['countView']>((id) => {
    if (viewedRef.current.has(id)) return;
    viewedRef.current.add(id);
    setSnapshot((prev) => ({
      ...prev,
      listings: prev.listings.map((entry) =>
        entry.id === id ? {...entry, views: entry.views + 1} : entry,
      ),
    }));
    void listingsApi
      .incrementViews(id)
      .then((updated) => {
        setSnapshot((prev) => ({...prev, listings: upsert(prev.listings, updated)}));
      })
      .catch(() => undefined);
  }, []);

  /* --------------------------------------------------------- saved/compare */

  const toggleSaved = useCallback<StoreValue['toggleSaved']>(
    (id) => {
      const current = userRef.current;
      if (!current) {
        notify('Đăng nhập để lưu tin này', 'info');
        return;
      }
      const on = !snapshotRef.current.saved.includes(id);
      setSnapshot((prev) => ({
        ...prev,
        saved: on ? [id, ...prev.saved] : prev.saved.filter((entry) => entry !== id),
      }));
      notify(on ? 'Đã lưu tin' : 'Đã bỏ lưu');
      void savesApi.set(id, on).catch((error) => {
        notify(`Không lưu được — ${message(error)}`, 'warn');
        void refresh();
      });
    },
    [notify, refresh],
  );

  const toggleCompare = useCallback<StoreValue['toggleCompare']>(
    (id) => {
      setCompare((prev) => {
        if (prev.includes(id)) return prev.filter((entry) => entry !== id);
        if (prev.length >= MAX_COMPARE) {
          notify(`So sánh tối đa ${MAX_COMPARE} tin cùng lúc`, 'info');
          return prev;
        }
        return [...prev, id];
      });
    },
    [notify, setCompare],
  );

  const clearCompare = useCallback(() => setCompare([]), [setCompare]);

  /* ------------------------------------------------------------- inquiries */

  const sendInquiry = useCallback<StoreValue['sendInquiry']>(
    async (input) => {
      const inquiry: Inquiry = {...input, at: new Date().toISOString(), id: newId('iq'), status: 'moi'};
      await mutate(
        (prev) => ({...prev, inquiries: [inquiry, ...prev.inquiries]}),
        async () => {
          const saved = await inquiriesApi.create(inquiry);
          setSnapshot((prev) => ({...prev, inquiries: upsert(prev.inquiries, saved)}));
        },
        'Không gửi được liên hệ',
      );
      notify('Đã gửi liên hệ tới người đăng tin');
    },
    [mutate, notify],
  );

  const setInquiryStatus = useCallback<StoreValue['setInquiryStatus']>(
    async (id, status) => {
      const current = snapshotRef.current.inquiries.find((entry) => entry.id === id);
      if (!current) return;
      const next = {...current, status};
      await mutate(
        (prev) => ({...prev, inquiries: upsert(prev.inquiries, next)}),
        async () => {
          const saved = await inquiriesApi.setStatus(id, status);
          setSnapshot((prev) => ({...prev, inquiries: upsert(prev.inquiries, saved)}));
        },
        'Không cập nhật được liên hệ',
      );
    },
    [mutate],
  );

  const resetDemo = useCallback(async () => {
    await devApi.reset();
    setCompare([]);
    viewedRef.current.clear();
    await refresh();
    notify('Đã khôi phục dữ liệu mẫu', 'info');
  }, [notify, refresh, setCompare]);

  const profileOf = useCallback(
    (id: string) => snapshotRef.current.profiles.find((entry) => entry.id === id) ?? null,
    [],
  );

  const value = useMemo<StoreValue>(
    () => ({
      ...snapshot,
      canPost: user?.role === 'nguoi-ban',
      clearCompare,
      compare,
      countView,
      profile: user,
      profileOf,
      ready,
      removeListing,
      resetDemo,
      saveListing,
      sendInquiry,
      setInquiryStatus,
      signIn,
      signOut,
      signUp,
      toggleCompare,
      toggleSaved,
      updateProfile,
      user,
    }),
    [
      clearCompare,
      compare,
      countView,
      profileOf,
      ready,
      removeListing,
      resetDemo,
      saveListing,
      sendInquiry,
      setInquiryStatus,
      signIn,
      signOut,
      signUp,
      snapshot,
      toggleCompare,
      toggleSaved,
      updateProfile,
      user,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
